import { isServer } from "@/shared/lib/utils";
import {
  getClientSideGraphQLClient,
  getServerSideGraphQLClient,
} from "./client";
import { ClientError } from "graphql-request";

const TOKEN_EXPIRED_ERROR = "Token expired";
const INVALID_TOKEN_ERROR = "Invalid Token";
/**
 * Custom GraphQL Error class to handle token expiration and other GraphQL errors.
 * This is extremely handy when we want to do condition based operations when we
 * have GraphQL errors.
 *
 * One instance is in the useConnectedBankAccounts hook.
 */
export class CustomGraphQLError extends Error {
  constructor(
    public message: string,
    public operationName?: string,
    public originalError?: ClientError,
    public isTokenExpired?: boolean,
  ) {
    super(message);
    this.name = "CustomGraphQLError";
  }
}

export type RequestOptions = {
  queryKey: string[];
  operationName?: string;
  variables: Record<string, unknown>;
  formData?: FormData;
};

export const fetchGraphQL = async <T>({
  query,
  operationName,
  token,
  customHeaders,
  variables,
  onTokenExpired,
  impersonateFamily,
}: {
  query: string;
  variables?: Record<string, unknown>;
  token?: string;
  customHeaders?: Record<string, string>;
  operationName?: string; // For better observability.
  // TODO: See if we can have a better way when dealing with client impersonation.
  impersonateFamily?: string;
  onTokenExpired: () => void;
}): Promise<T> => {
  if (!token) {
    token =
      typeof window !== "undefined"
        ? localStorage.getItem("give-session-token") || ""
        : "";
  }

  const client = isServer()
    ? await getServerSideGraphQLClient(token as string, customHeaders)
    : getClientSideGraphQLClient(
        token as string,
        impersonateFamily,
        customHeaders,
      );

  try {
    return await client.request<T>(query, variables);
  } catch (error) {
    // Type guard for ClientError
    if (error instanceof ClientError) {
      const isInvalidToken = error.message.includes(INVALID_TOKEN_ERROR);
      if (isInvalidToken) {
        // show a toast and move out
        console.log("Invalid token");
        onTokenExpired();
      }

      const isTokenExpired = error.message.includes(TOKEN_EXPIRED_ERROR);

      if (isTokenExpired) {
        // show a toast and move out
        console.log("Token expired");
        onTokenExpired();
      }

      // Log detailed error information
      console.error("GraphQL Error:", {
        operation: operationName,
        message: error.message,
        query: query.substring(0, 100) + "...", // Log first 100 chars of query
        variables: variables instanceof FormData ? "FormData" : variables,
        fullError: error,
      });

      throw new CustomGraphQLError(
        error.message,
        operationName,
        error,
        isTokenExpired,
      );
    }

    // Handle unknown errors
    console.error("Unknown GraphQL Error:", error);
    throw new CustomGraphQLError(
      "An unexpected error occurred while processing your request",
      operationName,
    );
  }
};
